import { useCallback, useContext, useMemo } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { SessionContext } from "../../common/contexts/SessionContext";
import { InputFormField } from "../../common/formfields/InputFormField";
import { FormButtons } from "../../common/forms/FormButtons";
import { FormHeader } from "../../common/forms/FormHeader";
import styles from "../../common/forms/forms.module.css";
import { Dataset, datasetsConstants } from "../../api/Datasets";
import { ProjectsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/ProjectsVirtualizedSelectForm";
import { SingleDatePickerFormField } from "../../common/formfields/DatePickerFormField/SingleDatePickerFormField";
import {
  EntityCustomTypeForm,
  useCustomTypeForm,
  useCustomTypeResolver,
} from "../../Customization/CustomTypes/generics/useCustomTypeForm";
import { CustomTypedEntityFormProps } from "../../common/entity/EntityInterfaces";
import { FormFieldLayout } from "../../common/formfields/FormFieldLayouts";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { calculateDirtyFields } from "../../common/forms/utils/FormUtils";

export const datasetClaimFormSchema = {
  name: yup
    .string()
    .required("Dataset name is required")
    .typeError("Dataset name is required")
    .test({
      test: (value) => value === value?.trim(),
      message: "Whitespaces at the beginning or at the end of the name are not allowed",
    }),
  projects: yup
    .array()
    .of(yup.object().shape({ id: yup.number().required().positive().integer(), name: yup.string().required() }))
    .min(1, "At least one project is required")
    .typeError("At least one project is required"),
};

const iso2local = (isodate: string | undefined | null) => {
  if (isodate) {
    return new Date(isodate).toISOString().slice(0, -1);
  } else {
    return "";
  }
};

export const datasetFormSchema = {};

export interface DatasetFormProps extends Omit<CustomTypedEntityFormProps<"datasets">, "event"> {
  event: CustomTypedEntityFormProps<"datasets">["event"] | "CLAIM";
}
export const DatasetForm = ({
  fieldLabels,
  permissions,
  title,
  subtitle,
  initialValues,
  onSubmit,
  onCancel,
  loading,
  submitButtonLabel,
  event,
  typeId,
  typesOverride,
}: DatasetFormProps) => {
  const { session } = useContext(SessionContext);

  const prepareEntity = useCallback(
    (entity: Partial<Dataset>) => {
      // We need to add back the time zone info
      entity.creationDate = entity.creationDate + "Z";

      if (entity.name !== entity.automaticName) {
        entity.isManuallyNamed = true;
      } else {
        entity.isManuallyNamed = false;
      }

      // Perform the claiming if in the claim context
      if (event === "CLAIM") {
        if (session?.person) {
          entity.claimed = true;
          entity.owner = session.person;
        } else {
          entity.claimed = false;
        }
        entity.isViewableEntity = false;
      }
      return entity;
    },
    [session?.person, event]
  );

  const _initialValues = useMemo(() => {
    if (initialValues)
      return {
        ...initialValues,
        creationDate: iso2local(initialValues.creationDate),
        operators: event === "CLAIM" && !!session?.person ? [session.person] : undefined,
      } as Partial<Dataset>;
  }, [initialValues, session?.person, event]);

  const { typedResolver } = useCustomTypeResolver<Partial<Dataset>>(
    event === "CLAIM" ? datasetClaimFormSchema : datasetFormSchema
  );

  // const { defaultValues, typedFormSchema, processCustomFields, type, setType, types } = useCustomTypeForm({
  //   initialValues: _initialValues,
  //   formSchema: event === "CLAIM" ? datasetClaimFormSchema : datasetFormSchema,
  //   typeId: typeId,
  //   entityType: "Dataset",
  // });

  const {
    reset,
    getValues,
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<Partial<Dataset>>({
    defaultValues: _initialValues,
    resolver: typedResolver,
  });
  const { processCustomFields, types } = useCustomTypeForm({
    control,
    reset,
    getValues,
    entityType: "Dataset",
    typeId,
    typesOverride,
  });

  const currentName = watch("name");

  return (
    <>
      <FormHeader title={`${event === "CLAIM" ? "Claim dataset" : title}`} subtitle={subtitle} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
        autoComplete="off"
        className={`form-horizontal ${styles.form_holder}`}
      >
        <fieldset>
          <legend className="col-md-offset-2 col-md-10">Details</legend>
        </fieldset>

        <InputFormField
          id={"name"}
          label={fieldLabels.name}
          errors={errors}
          register={register}
          autoFocus={false}
          required={false}
          // readOnly={true}
        />

        {!!initialValues?.isManuallyNamed && currentName !== initialValues.automaticName && (
          <FormFieldLayout id="" label="" horizontal>
            <div className="flex align-center gap-5 form form-control" style={{ justifyContent: "space-between" }}>
              <div className="ellipsisContainer" style={{ color: "var(--gray-400)" }}>
                <span>{initialValues.automaticName}</span>
              </div>
              <button
                onClick={() => setValue("name", initialValues?.automaticName)}
                className="flex align-center gap-5 btn btn-ghost-secondary btn-xs"
              >
                <LucideIcon name="refresh-ccw" />
                Restore {fieldLabels.automaticName.toLowerCase()}
              </button>
            </div>
          </FormFieldLayout>
        )}

        <SingleDatePickerFormField
          type={"datetime"}
          id={"creationDate"}
          label={fieldLabels.creationDate}
          control={control}
          required={false}
          errors={errors}
          readOnly={true}
          horizontal
        />

        <ProjectsVirtualizedSelectForm
          id={"projects"}
          label={fieldLabels.projects}
          control={control}
          required={event === "CLAIM"}
          isMulti
          horizontal
          allowCreateEntity
          allowUnassigned={event !== "CLAIM"}
          showControls
          filters={{ currentUserHasAddPermission: true }}
        />

        <EntityCustomTypeForm
          entityType="Dataset"
          types={types}
          control={control}
          setValue={setValue}
          initialValues={_initialValues}
          errors={errors}
          entityConstants={datasetsConstants}
        />

        <FormButtons
          onClose={onCancel}
          onSubmit={handleSubmit(async (entity) => await onSubmit(processCustomFields(prepareEntity(entity))))}
          disabled={isSubmitting}
          errors={errors}
          submitButtonLabel={`${event === "CLAIM" ? "Claim dataset" : submitButtonLabel}`}
          loading={loading}
          reset={() => reset(_initialValues)}
          dirtyFields={["EDIT", "CLONE"].includes(event) ? calculateDirtyFields(dirtyFields) : undefined}
        />
      </form>
    </>
  );
};
